/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const WaterCtor = (typeof globalThis !== "undefined" && (globalThis.SimpleWaterNode || globalThis.SimpleWater)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const WATER_PROPS = [
  "opacity",
  "color",
  "tideHeight",
  "tideScale",
  "tideSpeed",
  "waveHeight",
  "waveScale",
  "waveSpeed",
  "ripplesSpeed",
  "ripplesScale"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class WaterEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    WATER_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);

          // Special handling for color
          if (prop === "color") {
            const color = this.sourceObject.color && this.sourceObject.color.isColor
              ? this.sourceObject.color
              : new THREE.Color();
            try {
              color.set(v);
              this._values.set(prop, color); // Store the THREE.Color object
              const originalDescriptor = this._originalDescriptors.get(prop);
              if (originalDescriptor && originalDescriptor.set) {
                originalDescriptor.set.call(this.sourceObject, color);
              } else {
                this.sourceObject.color = color;
              }
            } catch (e) {
              console.warn(`Failed to set color for ${prop}:`, e);
            }
            return;
          }

          // Special handling for Vector2 fields
          const isVec2 =
            prop === "tideScale" ||
            prop === "tideSpeed" ||
            prop === "waveScale" ||
            prop === "waveSpeed";

          if (isVec2) {
            const vec2 = this.sourceObject[prop] && this.sourceObject[prop].isVector2
              ? this.sourceObject[prop]
              : new THREE.Vector2();
            try {
              if (Array.isArray(v)) {
                vec2.set(v[0], v[1]);
              } else if (typeof v === "object") {
                vec2.set(v.x, v.y);
              }
              this._values.set(prop, vec2); // Store the THREE.Vector2 object
              const originalDescriptor = this._originalDescriptors.get(prop);
              if (originalDescriptor && originalDescriptor.set) {
                originalDescriptor.set.call(this.sourceObject, vec2);
              } else {
                this.sourceObject[prop] = vec2;
              }
            } catch (e) {
              console.warn(`Failed to set Vector2 for ${prop}:`, e);
            }
            return;
          }

          // Default property setting
          const originalDescriptor = this._originalDescriptors.get(prop);
          if (originalDescriptor && originalDescriptor.set) {
            try {
              originalDescriptor.set.call(this.sourceObject, v);
            } catch (e) {
              console.warn(`Failed to call original setter for ${prop}:`, e);
            }
          } else {
            try {
              this.sourceObject[prop] = v;
            } catch (e) {
              // ignore
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class WaterAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const COMPONENT = "simple-water";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (Object.keys(pending).length > 0) {
        const current = { ...(el.getAttribute(COMPONENT) || {}) };
        Object.assign(current, pending);
        el.setAttribute(COMPONENT, current);
        Object.keys(pending).forEach(key => delete pending[key]);
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      if (e?.detail?.name === COMPONENT) {
        flush();
      }
    });

    const setComponentProp = (key, val) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(COMPONENT);
      if (!attr) {
        pending[key] = val;
      } else {
        const current = { ...attr };
        current[key] = val;
        el.setAttribute(COMPONENT, current);
      }
    };

    const getComponentProp = key => {
      const el = this.sourceObject?.el;
      if (!el) return pending[key];
      const attr = el.getAttribute(COMPONENT);
      return (attr && attr[key]) ?? pending[key];
    };

    WATER_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(prop),
        set: v => setComponentProp(prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of WATER_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new WaterAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new WaterEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Main WaterElement class                                                    */
/* ────────────────────────────────────────────────────────────────────────── */
export class WaterElement extends ScriptableObject {
  static scriptableType = "simple-water";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("simple-water", {
        opacity: 1,
        color: "#0054df",
        tideHeight: 0.01,
        tideScale: { x: 1, y: 1 },
        tideSpeed: { x: 0.5, y: 0.5 },
        waveHeight: 0.1,
        waveScale: { x: 1, y: 20 },
        waveSpeed: { x: 0.05, y: 6 },
        ripplesSpeed: 0.25,
        ripplesScale: 1,
        ...initial
      });

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }
      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createWaterElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public water props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (prop === "color" && val?.isColor) {
        this[prop] = val.clone();
      } else if (prop === "tideScale" || prop === "tideSpeed" || prop === "waveScale" || prop === "waveSpeed") {
        if (val?.isVector2) {
          this[prop] = val.clone();
        } else if (Array.isArray(val)) {
          this[prop] = new THREE.Vector2(val[0], val[1]);
        } else if (typeof val === "object") {
          this[prop] = new THREE.Vector2(val.x, val.y);
        } else {
          this[prop] = val;
        }
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of WATER_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of source.children || []) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of WATER_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (p === "color" && v?.isColor) {
        init[p] = v.clone();
      } else if (p === "tideScale" || p === "tideSpeed" || p === "waveScale" || p === "waveSpeed") {
        if (v?.isVector2) {
          init[p] = v.clone();
        } else if (Array.isArray(v)) {
          init[p] = v.slice();
        } else if (typeof v === "object") {
          init[p] = { x: v.x, y: v.y };
        } else {
          init[p] = v;
        }
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor = this.constructor && typeof this.constructor.create === "function" ? this.constructor : WaterElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new WaterElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* DRY helper: upgrade a plain Editor SimpleWaterNode instance to Scriptable */
/* ────────────────────────────────────────────────────────────────────────── */
function makeEditorWaterNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || WaterCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: WaterElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = WaterElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new WaterEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new WaterEditorAdapter(node, descriptors);
  }

  for (const prop of WATER_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
WATER_PROPS.forEach(prop => {
  Object.defineProperty(WaterElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createWaterElementEditor(initial = {}) {
  const WaterCtor = globalThis.SimpleWaterNode;

  if (typeof WaterCtor !== "function") {
    throw new Error("WaterElementEditor: SimpleWaterNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new WaterCtor(editor);
  } catch (e) {
    throw new Error(`WaterElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorWaterNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("simple-water", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== WaterElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return WaterElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorWaterNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(WaterElement.prototype, Symbol.toStringTag, { value: "WaterElement" });

// Named exports expected by external consumers
export { WaterAFrameAdapter, WaterEditorAdapter };
